import { WarningCircle } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button, Input, PasswordInput } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

export interface SignInFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
  error?: string | null;
  onToggleFlow: () => void;
  onForgotPassword?: () => void;
}

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export function SignInForm({
  onSubmit,
  isLoading = false,
  error,
  onToggleFlow,
  onForgotPassword,
}: SignInFormProps) {
  const { t } = useTranslation(["auth"]);

  return (
    <div className="flex flex-col">
      {/* Header */}
      <motion.div custom={0} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("auth:signIn.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("auth:signIn.subtitle")}
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5">
        {/* Email */}
        <motion.div
          custom={1}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="email"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:signIn.emailLabel")}
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={t("auth:signIn.emailPlaceholder")}
            required
            autoComplete="email"
          />
        </motion.div>

        {/* Password */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="password"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:signIn.passwordLabel")}
          </label>
          <PasswordInput
            id="password"
            name="password"
            placeholder={t("auth:signIn.passwordPlaceholder")}
            required
            autoComplete="current-password"
            showLabel={t("auth:password.show")}
            hideLabel={t("auth:password.hide")}
          />
        </motion.div>

        {/* Forgot password */}
        {onForgotPassword && (
          <motion.div
            custom={3}
            variants={stagger}
            initial="hidden"
            animate="show"
            className="flex justify-end"
          >
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-[13px] font-medium text-secondary hover:text-accent transition-colors"
            >
              {t("auth:signIn.forgotPassword")}
            </button>
          </motion.div>
        )}

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5"
          >
            <WarningCircle
              className="mt-0.5 size-4 shrink-0 text-red-500"
              weight="fill"
            />
            <p className="text-[13px] text-red-600">{error}</p>
          </motion.div>
        )}

        {/* Submit */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            className="mt-1 w-full"
          >
            {t("auth:signIn.submit")}
          </Button>
        </motion.div>

        {/* Toggle */}
        <motion.p
          custom={5}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="text-center text-[13px] text-secondary"
        >
          {t("auth:signIn.noAccount")}{" "}
          <button
            type="button"
            onClick={onToggleFlow}
            className="font-medium text-primary hover:text-accent transition-colors"
          >
            {t("auth:signIn.signUpLink")}
          </button>
        </motion.p>
      </form>
    </div>
  );
}
